import getConfig from 'next/config'
import { createClient } from 'contentful'
import { NextApiRequestCookies } from 'next/dist/server/api-utils'

import { LineOfBusinessSpace } from './src/siteBuilder/lineOfBusiness/types'
import type { TimelinePreviewOption } from './src/siteBuilder/utils/toTimelinePreviewOption'

// ENVIRONMENT VARIABLES
export const {
  IS_PROD,
  CONTENTFUL_SB2_CLIENT_CONFIG,
  CONTENTFUL_BLOG_BUILDER_CLIENT_CONFIG,
  CONTENTFUL_SB2_SLUG_CACHE_CLIENT_CONFIG,
  GLOBAL_ELEMENTS_HOST,
} = getConfig()?.serverRuntimeConfig

function optanonConsentAllow(cookie: string): string {
  if (cookie) {
    const regex = /groups=[^&]+/g
    const groups = cookie.match(regex)

    return groups ? groups.toString() : ''
  }
  return undefined
}

export const { TAG_MANAGER_URL, CONTENTFUL_BLOG_BUILDER_SPACE, CONTENTFUL_BLOG_BUILDER_DELIVERY_TOKEN } =
  getConfig()?.publicRuntimeConfig

export const siteBuilder: LineOfBusinessSpace = {
  label: 'Site Builder 2.0',
  lineOfBusiness: 'siteBuilder',
  get options() {
    return {
      client: createClient(CONTENTFUL_SB2_CLIENT_CONFIG).withoutUnresolvableLinks,
      slugCacheClient: createClient(CONTENTFUL_SB2_SLUG_CACHE_CLIENT_CONFIG).withoutUnresolvableLinks,
    }
  },
  getClient: (
    cookies: NextApiRequestCookies,
    transactionId: string,
    timelinePreviewOption?: TimelinePreviewOption | null
  ) => {
    const optanonConsent = optanonConsentAllow(cookies.OptanonConsent)

    const filteredCookies = {
      'AMCV_67A50FC0539F0BBD0A490D45%40AdobeOrg': cookies['AMCV_67A50FC0539F0BBD0A490D45%40AdobeOrg'],
      referer: cookies.referer,
      agent: cookies.agent,
      ipAddress: cookies.ipAddress,
      kndctrIdentity: cookies['kndctr_67A50FC0539F0BBD0A490D45_AdobeOrg_identity'],
      kndctrCluster: cookies['kndctr_67A50FC0539F0BBD0A490D45_AdobeOrg_cluster'],
      OptanonConsent: optanonConsent,
    }

    const stringifiedFilteredCookies = JSON.stringify(filteredCookies)

    const clientConfig = Object.assign({}, CONTENTFUL_SB2_CLIENT_CONFIG, {
      headers: {
        cookies: stringifiedFilteredCookies,
        'correlation-id': transactionId,
        'x-resolve-experiences': 'false',
      },
    })

    if (timelinePreviewOption) {
      // Timeline preview routes through the pers-api-contentful-proxy host (same as normal traffic,
      // already set on clientConfig) so activity containers resolve. The proxy forwards Timeline
      // requests to preview.contentful.com upstream. Contentful SDK >= 11.12.4 opts out of its
      // CPA-host check for custom (non-`*.contentful.com`) hosts, so the real proxy host passes
      // validation directly — no fake-host rewrite needed.
      clientConfig.timelinePreview = timelinePreviewOption
      // Fail fast-ish: the SDK default is 5 exponential-backoff retries (~20s) on 5xx/429. Show the
      // editor the error page in ~5s instead — but keep two retries so a transient blip can recover.
      clientConfig.retryLimit = 2
    }

    return createClient(clientConfig).withoutUnresolvableLinks
  },
}

export const blogBuilder = {
  label: 'Blog Builder',
  lineOfBusiness: 'blogBuilder',
  getClient: () => {
    return createClient(CONTENTFUL_BLOG_BUILDER_CLIENT_CONFIG).withoutUnresolvableLinks
  },
}

export default {
  IS_PROD,
  TAG_MANAGER_URL,
  space: siteBuilder,
  blog: blogBuilder,
}
